export type FirmwareConnectionType = 'can' | 'serial' | 'manual';

export type FirmwareFilenameValidationResult = {
  isValid: boolean;
  error?: string;
  firmwareVersion?: string;
  buildType?: FirmwareConnectionType | 'serial';
  firmwareProcessType?: 'welding' | 'coating' | null;
};

const ConnectionTypeEnum = {
  CAN: 'can',
  SERIAL: 'serial',
  MANUAL: 'manual',
} as const;

const MIN_WELDING_FIRMWARE = '4.1.1';
const MIN_COATING_FIRMWARE = '1.0.3';

export const normalizeFirmwarePlatform = (
  platform: string | undefined
): string | null => {
  if (!platform) return null;
  const lowerPlatform = platform.toLowerCase();
  if (
    lowerPlatform.includes('imx8a') ||
    lowerPlatform.includes('alpha') ||
    lowerPlatform.includes('8a')
  ) {
    return 'imx_8a';
  }
  if (
    lowerPlatform.includes('imx8b') ||
    lowerPlatform.includes('beta') ||
    lowerPlatform.includes('8b')
  ) {
    return 'imx_8b';
  }
  if (
    lowerPlatform.includes('imx8ivy') ||
    lowerPlatform.includes('ivy') ||
    lowerPlatform.includes('8ivy')
  ) {
    return 'imx_8ivy';
  }
  if (lowerPlatform === '8a' || lowerPlatform === 'imx_8a') return 'imx_8a';
  if (lowerPlatform === '8b' || lowerPlatform === 'imx_8b') return 'imx_8b';
  if (lowerPlatform === '8ivy' || lowerPlatform === 'imx_8ivy') return 'imx_8ivy';
  return null;
};

const parseVersion = (version: string): number[] => {
  const cleaned = version.replace(/^v/i, '').trim();
  return cleaned.split('.').map((num) => parseInt(num, 10) || 0);
};

/**
 * Validate welding/coating firmware zip filenames.
 *
 * Welding formats:
 * - CAN:    data360-can-{env}-v{version}-{platform}.zip
 * - Manual: data360-manual-{env}-v{version}-{platform}.zip
 * - Serial: data360-{env}-v{version}-{platform}.zip
 *           data360-serial-{env}-v{version}-{platform}.zip
 * Coating:  data360-coating-{env}-v{version}.zip
 */
export function validateFirmwareFilename(
  fileName: string,
  environment: string,
  processType: string,
  deviceType: string,
  connectionType?: string
): FirmwareFilenameValidationResult {
  const nameWithoutExt = fileName.replace(/\.zip$/i, '');
  const parts = nameWithoutExt.split('-');

  if (!fileName.toLowerCase().endsWith('.zip')) {
    return { isValid: false, error: 'Firmware file must be a .zip file' };
  }

  let envIndex = 1;
  let buildType: FirmwareConnectionType = 'serial';
  let firmwareProcessType: 'welding' | 'coating' | null = null;

  if (parts[0] === 'data360' && parts[1] === 'can') {
    buildType = 'can';
    envIndex = 2;
    firmwareProcessType = 'welding';
  } else if (parts[0] === 'data360' && parts[1] === 'manual') {
    buildType = 'manual';
    envIndex = 2;
    firmwareProcessType = 'welding';
  } else if (parts[0] === 'data360' && parts[1] === 'serial') {
    buildType = 'serial';
    envIndex = 2;
    firmwareProcessType = 'welding';
  } else if (parts[0] === 'data360' && parts[1] === 'coating') {
    buildType = 'serial';
    envIndex = 2;
    firmwareProcessType = 'coating';
  } else if (parts[0] === 'data360') {
    buildType = 'serial';
    envIndex = 1;
    firmwareProcessType = 'welding';
  }

  const deviceProcessTypeLower = processType?.toLowerCase();
  if (
    firmwareProcessType &&
    deviceProcessTypeLower &&
    deviceProcessTypeLower !== '-' &&
    firmwareProcessType !== deviceProcessTypeLower
  ) {
    return {
      isValid: false,
      error: `Process type mismatch: The firmware file is for ${firmwareProcessType} devices, but this device is a ${deviceProcessTypeLower} device. Please select a firmware file that matches the device type.`,
      buildType,
      firmwareProcessType,
    };
  }

  if (parts[envIndex] !== environment) {
    return {
      isValid: false,
      error: `Firmware environment (${parts[envIndex]}) does not match current environment (${environment})`,
      buildType,
      firmwareProcessType,
    };
  }

  const versionIndex = envIndex + 1;
  if (!parts[versionIndex] || !parts[versionIndex].startsWith('v')) {
    return {
      isValid: false,
      error: 'Invalid version format. Version must start with "v"',
      buildType,
      firmwareProcessType,
    };
  }

  const firmwareVersion = parts[versionIndex].replace(/^v/i, '');

  let firmwarePlatform: string | null = null;
  if (firmwareProcessType === 'welding') {
    const platformIndex = versionIndex + 1;
    if (!parts[platformIndex]) {
      return {
        isValid: false,
        error: 'Platform information missing in filename',
        buildType,
        firmwareProcessType,
        firmwareVersion,
      };
    }
    firmwarePlatform = normalizeFirmwarePlatform(parts[platformIndex]);
    if (!firmwarePlatform) {
      return {
        isValid: false,
        error: `Invalid platform in filename: ${parts[platformIndex]}. Platform must be 8a, 8b, or 8ivy.`,
        buildType,
        firmwareProcessType,
        firmwareVersion,
      };
    }
  }

  const devicePlatformNormalized = normalizeFirmwarePlatform(deviceType);
  if (!devicePlatformNormalized) {
    return {
      isValid: false,
      error: `Device platform (${deviceType}) is not recognized. Supported platforms: 8a, 8b, 8ivy.`,
      buildType,
      firmwareProcessType,
      firmwareVersion,
    };
  }

  if (firmwarePlatform && firmwarePlatform !== devicePlatformNormalized) {
    return {
      isValid: false,
      error: `Platform mismatch: Firmware file is for platform ${firmwarePlatform}, but this device is platform ${devicePlatformNormalized}. Please select a firmware file that matches the device platform.`,
      buildType,
      firmwareProcessType,
      firmwareVersion,
    };
  }

  if (firmwareProcessType === 'welding' && deviceProcessTypeLower === 'welding') {
    if (connectionType) {
      if (connectionType === ConnectionTypeEnum.CAN && buildType !== 'can') {
        return {
          isValid: false,
          error: `Weld type mismatch: This device uses CAN (M500) weld type, but the firmware file is for ${buildType} weld type. CAN devices can only upload CAN firmware files (data360-can-...). Please select a CAN firmware file.`,
          buildType,
          firmwareProcessType,
          firmwareVersion,
        };
      }
      if (connectionType === ConnectionTypeEnum.SERIAL && buildType !== 'serial') {
        return {
          isValid: false,
          error: `Weld type mismatch: This device uses Serial (P600Z/P625) weld type, but the firmware file is for ${buildType} weld type. Serial devices can only upload Serial firmware files (data360-... or data360-serial-...). Please select a Serial firmware file.`,
          buildType,
          firmwareProcessType,
          firmwareVersion,
        };
      }
      if (connectionType === ConnectionTypeEnum.MANUAL && buildType !== 'manual') {
        return {
          isValid: false,
          error: `Weld type mismatch: This device uses Manual weld type, but the firmware file is for ${buildType} weld type. Manual devices can only upload Manual firmware files (data360-manual-...). Please select a Manual firmware file.`,
          buildType,
          firmwareProcessType,
          firmwareVersion,
        };
      }
    }
  }

  const minRequiredVersion =
    firmwareProcessType === 'welding'
      ? MIN_WELDING_FIRMWARE
      : firmwareProcessType === 'coating'
        ? MIN_COATING_FIRMWARE
        : null;

  if (minRequiredVersion) {
    const v1 = parseVersion(firmwareVersion);
    const v2 = parseVersion(minRequiredVersion);

    if (v1[0] < v2[0]) {
      return {
        isValid: false,
        error: `${firmwareProcessType === 'welding' ? 'Welding' : 'Coating'} device: Firmware version must be ≥ ${minRequiredVersion}. Selected file version: ${firmwareVersion}`,
        firmwareVersion,
        buildType,
        firmwareProcessType,
      };
    }

    if (v1[0] === v2[0]) {
      const minor1 = v1[1] || 0;
      const minor2 = v2[1] || 0;
      const patch1 = v1[2] || 0;
      const patch2 = v2[2] || 0;
      if (minor1 < minor2 || (minor1 === minor2 && patch1 < patch2)) {
        return {
          isValid: false,
          error: `${firmwareProcessType === 'welding' ? 'Welding' : 'Coating'} device: Firmware version must be ≥ ${minRequiredVersion}. Selected file version: ${firmwareVersion}`,
          firmwareVersion,
          buildType,
          firmwareProcessType,
        };
      }
    }
  }

  return {
    isValid: true,
    firmwareVersion,
    buildType,
    firmwareProcessType,
  };
}
